import type { PermName } from './permissions.js';

export interface PermissionMeta {
  name: PermName;
  /** Group the role editor renders as a collapsible section. */
  group: string;
  label: string;
  description: string;
}

export const PERMISSION_CATALOG: readonly PermissionMeta[] = [
  {
    name: 'TENANTS_READ',
    group: 'Tenants',
    label: 'View tenants',
    description: 'Read the tenant list and tenant detail.',
  },
  {
    name: 'TENANT_NOTES_WRITE',
    group: 'Tenants',
    label: 'Write tenant notes',
    description: 'Create, edit and pin notes on a tenant.',
  },
  {
    name: 'TENANT_STATUS_WRITE',
    group: 'Tenants',
    label: 'Change tenant status',
    description: 'Suspend or reactivate a tenant.',
  },
  {
    name: 'TENANTS_CREATE',
    group: 'Tenants',
    label: 'Provision tenants',
    description: 'Create a new tenant.',
  },
  {
    name: 'TENANT_ORG_WRITE',
    group: 'Tenants',
    label: 'Edit tenant organization',
    description: 'Create and edit locations, profit centers and terminals.',
  },
  {
    name: 'TENANT_USERS_MANAGE',
    group: 'Tenants',
    label: 'Manage tenant users',
    description: 'Create, edit and deactivate a tenant’s users and roles.',
  },

  {
    name: 'ENTITLEMENTS_READ',
    group: 'Entitlements',
    label: 'View entitlements',
    description: 'Read module entitlements and their history.',
  },
  {
    name: 'ENTITLEMENTS_WRITE',
    group: 'Entitlements',
    label: 'Change entitlements',
    description: 'Grant or revoke module access.',
  },
  {
    name: 'ENTITLEMENTS_LOCK',
    group: 'Entitlements',
    label: 'Lock entitlements',
    description: 'Set or clear an entitlement’s locked mode.',
  },
  {
    name: 'ENTITLEMENTS_PROMOTIONAL',
    group: 'Entitlements',
    label: 'Grant promotional access',
    description: 'Grant entitlements with source=promotional.',
  },
  {
    name: 'REGULATED_PROFILE_WRITE',
    group: 'Entitlements',
    label: 'Record regulated profile',
    description:
      'Record or correct a tenant’s regulated profile; it turns HeyRoot’s regulated-industry guards on or off. Needs View entitlements as well.',
  },

  {
    name: 'USERS_READ',
    group: 'Users & Staff',
    label: 'View users',
    description: 'Search people across tenants and read their memberships.',
  },
  {
    name: 'USERS_MANAGE',
    group: 'Users & Staff',
    label: 'Manage users',
    description: 'Send password resets and suspend accounts.',
  },
  {
    name: 'STAFF_READ',
    group: 'Users & Staff',
    label: 'View staff',
    description: 'Read the internal admin-operator list.',
  },
  {
    name: 'STAFF_MANAGE',
    group: 'Users & Staff',
    label: 'Manage staff',
    description: 'Invite, re-role, suspend and delete admin operators.',
  },
  {
    name: 'ROLES_MANAGE',
    group: 'Users & Staff',
    label: 'Manage admin roles',
    description: 'Create and edit admin roles and their permissions.',
  },

  {
    name: 'BUSINESS_TYPES_READ',
    group: 'Business Types',
    label: 'View business types',
    description: 'Read business types and their template versions.',
  },
  {
    name: 'BUSINESS_TYPES_WRITE',
    group: 'Business Types',
    label: 'Edit business types',
    description: 'Create and edit types, edit and publish template versions.',
  },

  {
    name: 'ASSISTANT_REVIEW',
    group: 'Assistant',
    label: 'Review escalations',
    description: 'Read escalations and submit review annotations.',
  },
  {
    name: 'ASSISTANT_CURATE',
    group: 'Assistant',
    label: 'Curate knowledge',
    description: 'Edit the assistant knowledge base.',
  },
  {
    name: 'ASSISTANT_RESOLVE',
    group: 'Assistant',
    label: 'Resolve escalations',
    description: 'Join escalation chat and resolve escalations.',
  },

  {
    name: 'ONBOARDING_VIEW',
    group: 'Onboarding',
    label: 'View onboarding',
    description: 'Read the onboarding queue and per-tenant state.',
  },
  {
    name: 'ONBOARDING_AUDIT_VIEW',
    group: 'Onboarding',
    label: 'View onboarding audit',
    description: 'Read the onboarding event log.',
  },
  {
    name: 'ONBOARDING_CLAIM',
    group: 'Onboarding',
    label: 'Claim tenants',
    description: 'Claim, renew and release a tenant.',
  },
  {
    name: 'ONBOARDING_OVERRIDE_CLAIM',
    group: 'Onboarding',
    label: 'Override claims',
    description: 'Release another operator’s active claim.',
  },
  {
    name: 'ONBOARDING_SNOOZE',
    group: 'Onboarding',
    label: 'Snooze onboarding',
    description: 'Snooze and unsnooze a tenant.',
  },
  {
    name: 'ONBOARDING_MARK_LIVE',
    group: 'Onboarding',
    label: 'Mark go-live',
    description: 'Call go-live on a tenant’s behalf.',
  },
  {
    name: 'ONBOARDING_RESCUE_EMAIL',
    group: 'Onboarding',
    label: 'Send rescue email',
    description: 'Manually trigger the onboarding rescue email.',
  },
  {
    name: 'ONBOARDING_RESYNC',
    group: 'Onboarding',
    label: 'Resync onboarding',
    description: 'Trigger a non-mutating onboarding resync scan.',
  },
  {
    name: 'ONBOARDING_STEP_OVERRIDE',
    group: 'Onboarding',
    label: 'Override steps',
    description: 'Force-complete or reopen an onboarding step.',
  },
  {
    name: 'ONBOARDING_PRODUCTION_APPLICATION_REVIEW',
    group: 'Onboarding',
    label: 'Review merchant applications',
    description: 'Record a CardPointe merchant-application approval or decline.',
  },

  {
    name: 'ACCOUNTING_MODULES_READ',
    group: 'Accounting',
    label: 'View accounting modules',
    description: 'Read cash/accrual accounting module state.',
  },
  {
    name: 'ACCOUNTING_MODULES_WRITE',
    group: 'Accounting',
    label: 'Edit accounting modules',
    description: 'Change cash/accrual accounting module state.',
  },

  {
    name: 'FEEDBACK_REVIEW',
    group: 'Feedback',
    label: 'View feedback',
    description: 'Read and open the feedback triage inbox.',
  },
  {
    name: 'FEEDBACK_RESOLVE',
    group: 'Feedback',
    label: 'Resolve feedback',
    description: 'Change status and edit internal notes.',
  },
  {
    name: 'FEEDBACK_TRIAGE',
    group: 'Feedback',
    label: 'Triage feedback',
    description: 'Score impact/effort, bulk-change status and export.',
  },
  {
    name: 'FEEDBACK_ERASE',
    group: 'Feedback',
    label: 'Erase feedback',
    description:
      'Permanently erase a feedback request and its screenshots on a verified privacy request. Cannot be undone.',
  },

  {
    name: 'COST_PLUS_READ',
    group: 'Cost Plus',
    label: 'View cost-plus',
    description: 'Read the messaging supplier catalog, buy rates and margin.',
  },
  {
    name: 'COST_PLUS_WRITE',
    group: 'Cost Plus',
    label: 'Edit cost-plus',
    description: 'Edit suppliers, buy rates and per-tenant plans.',
  },

  {
    name: 'PRICING_READ',
    group: 'Pricing',
    label: 'View pricing',
    description: 'Read plans, module pricing and tenant subscriptions.',
  },
  {
    name: 'PRICING_WRITE',
    group: 'Pricing',
    label: 'Edit pricing',
    description: 'Edit plans and module add-on pricing.',
  },
  {
    name: 'SUBSCRIPTIONS_WRITE',
    group: 'Pricing',
    label: 'Change subscriptions',
    description: 'Assign or change a tenant’s plan and seat count.',
  },

  {
    name: 'WORKER_READ',
    group: 'Worker',
    label: 'View worker',
    description: 'Read schedules, dead jobs, kind state, alerts, findings, queues and drills.',
  },
  {
    name: 'WORKER_OPERATE',
    group: 'Worker',
    label: 'Operate worker',
    description:
      'Requeue a dead job, pause or unpause a kind or schedule, run a schedule now and enqueue a fire drill. Every action is audited. Needs View worker as well.',
  },
] as const;

/** Groups in render order. */
export const PERMISSION_GROUPS: readonly string[] = [
  'Tenants',
  'Entitlements',
  'Users & Staff',
  'Business Types',
  'Assistant',
  'Onboarding',
  'Accounting',
  'Feedback',
  'Cost Plus',
  'Pricing',
  'Worker',
];
